import React, { useEffect, useState } from 'react';

type Props = {
  colors: any;
  pill: (bg: string, col?: string) => React.CSSProperties;
  buildPostBody?: () => any; // Optional builder {start,end,...}
  refreshMs?: number;        // Auto refresh interval
  healthyThreshold?: number; // Warning threshold
};

type StatsResponse = {
  spades_information?: {
    total_spades_installed?: number;
    total_spades_user_hold?: number;
    total_wear_out?: number;
    total_need_repair?: number;
    total_healthy_in_ware_house?: number;
    total_spades_in_ware_house?: number;
    total_spades?: number;
    spades_size_counts?: Array<{ Size?: number; size?: number; Count?: number; count?: number }>;
  };
};

// Helper for Persian digits
const toFa = (v: string | number) =>
  String(v)
    .replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])
    .replace(/\./g, '٫')
    .replace(/,/g, '٬');

const SystemHealthCard: React.FC<Props> = ({
  colors,
  pill,
  buildPostBody,
  refreshMs = 60000,
  healthyThreshold = 5,
}) => {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [healthyTotal, setHealthyTotal] = useState<number>(0);

  const timeFa = (d: Date) =>
    new Intl.DateTimeFormat('fa-IR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(d);

  const toRFC3339 = (d: Date) => d.toISOString().replace(/\.\d{3}Z$/, 'Z');
  const startOfUTC = (d: Date) =>
    new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0));
  const endOfUTC = (d: Date) =>
    new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59));

  const defaultBody = () => {
    const now = new Date();
    const s = new Date(now.getTime() - 7 * 86400000);
    return { start: toRFC3339(startOfUTC(s)), end: toRFC3339(endOfUTC(now)) };
  };

  const fetchStats = async () => {
    setLoading(true);
    setErr(null);

    const token = localStorage.getItem('token') || '';
    const headers: HeadersInit = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    try {
      const r = await fetch('/api/dashboard/stats', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(buildPostBody ? buildPostBody() : defaultBody()),
      });

      const t = await r.text();
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${t}`);

      const j: StatsResponse = t ? JSON.parse(t) : {};
      const info = j?.spades_information ?? {};
      const healthy = Number(info.total_healthy_in_ware_house ?? info.total_spades_in_ware_house ?? 0);

      setHealthyTotal(Number.isFinite(healthy) ? healthy : 0);
      setLastUpdated(new Date());
    } catch (e: any) {
      setErr(e?.message || 'خطای دریافت وضعیت سیستم');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    const id = setInterval(fetchStats, Math.max(15000, refreshMs));
    return () => clearInterval(id);
  }, [refreshMs, healthyThreshold]);

  const showHealthyWarn = healthyTotal < healthyThreshold;

  // ---- styles
  const card: React.CSSProperties = {
    background: colors.panel,
    border: `1px solid ${colors.border}`,
    borderRadius: 16,
    padding: 16,
    boxShadow: '0 6px 20px rgba(0,0,0,.25)',
  };
  const cardTitle: React.CSSProperties = { fontWeight: 800, marginBottom: 10, letterSpacing: 0.2 };

  return (
    <section style={card} dir="rtl">
      <div style={cardTitle}>سلامت سیستم و هشدارها</div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        {loading && <span style={pill('rgba(96,165,250,0.12)')}>در حال به‌روزرسانی…</span>}
        {err && <span style={pill('rgba(248,113,113,0.12)', colors.danger)}>{err}</span>}
        {lastUpdated && (
          <span style={pill('rgba(34,197,94,0.12)')}>
            به‌روزرسانی: {toFa(timeFa(lastUpdated))}
          </span>
        )}
      </div>

      <div style={{ display: 'grid', gap: 10 }}>
        {/*{showHealthyWarn ? (*/}
        {/*  <div style={pill('rgba(248,113,113,0.12)', colors.danger)}>*/}
        {/*    هشدار موجودی پایین — اسپید سالم در انبار: {toFa(healthyTotal)} (کمتر از{' '}*/}
        {/*    {toFa(healthyThreshold)})*/}
        {/*  </div>*/}
        {/*) : (*/}
        {/*  <div style={pill('rgba(52,211,153,0.12)')}>*/}
        {/*    موجودی اسپید سالم مناسب است — مقدار فعلی: {toFa(healthyTotal)}*/}
        {/*  </div>*/}
        {/*)}*/}

        <div style={pill('rgba(52,211,153,0.12)')}>
          سرویس‌ها پایدار هستند — آخرین همگام‌سازی موفق
        </div>
      </div>
    </section>
  );
};

export default SystemHealthCard;
